import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { Box, Input, Tooltip } from '@chakra-ui/react';
import type { InvoiceDetailResponse } from '../../../types/invoice';
import { fmtUsd } from './invoiceDetailFormFormatters';

export interface SummaryNumericFieldRendererDeps {
    isCurrentReturnInvoice: boolean;
    editingNumericField: string | null;
    editingNumericValues: Record<string, string>;
    setEditingNumericField: (v: string | null) => void;
    setEditingNumericValues: Dispatch<SetStateAction<Record<string, string>>>;
    getNumberValue: (field: keyof InvoiceDetailResponse) => number;
    updateField: (field: keyof InvoiceDetailResponse, value: unknown) => void;
    commitTaxesTotal?: (parsed: number) => void;
    locationDefaultTaxRate?: number | null;
    onTeachableAmountCommitted?: (field: 'taxesTotal' | 'orderTotal', value: number) => void;
    /** Value shown when the cell was opened, before keystrokes replace it. */
    summaryEditBaselineRef: MutableRefObject<Record<string, number>>;
}

export function createRenderSummaryNumericField(deps: SummaryNumericFieldRendererDeps) {
    const {
        isCurrentReturnInvoice,
        editingNumericField,
        editingNumericValues,
        setEditingNumericField,
        setEditingNumericValues,
        getNumberValue,
        updateField,
        commitTaxesTotal,
        locationDefaultTaxRate,
        onTeachableAmountCommitted,
        summaryEditBaselineRef,
    } = deps;

    const notifyAmountTeaching = (
        fieldName: keyof InvoiceDetailResponse,
        previous: number,
        parsed: number,
    ) => {
        if (fieldName !== 'taxesTotal' && fieldName !== 'orderTotal') return;
        if (Math.round(previous * 100) === Math.round(parsed * 100)) return;
        onTeachableAmountCommitted?.(fieldName, parsed);
    };

    const beginSummaryEdit = (fieldKey: string, baseline: number) => {
        summaryEditBaselineRef.current[fieldKey] = baseline;
        setEditingNumericField(fieldKey);
    };

    const previousSummaryValue = (fieldKey: string, fallback: number) => {
        const baseline = summaryEditBaselineRef.current[fieldKey];
        delete summaryEditBaselineRef.current[fieldKey];
        return baseline ?? fallback;
    };

    const taxesAutoTooltip =
        locationDefaultTaxRate != null
            ? `Auto: ${(locationDefaultTaxRate * 100).toLocaleString('en-US', { maximumFractionDigits: 3 })}% of above line items subtotal`
            : 'Click to edit';

    const commitTaxValue = (parsed: number) => {
        if (commitTaxesTotal) commitTaxesTotal(parsed);
        else updateField('taxesTotal', parsed);
    };

    function renderNumericField(fieldName: keyof InvoiceDetailResponse, _label?: string, fontWeight?: string, valueColor?: string) {
    const fieldKey = `summary-${fieldName}`;
    const currentValue = getNumberValue(fieldName);
    const isEditing = editingNumericField === fieldKey;
    const editingValue = editingNumericValues[fieldKey];
    const summaryTextColor = valueColor ?? 'gray.900';

    if (fieldName === 'discountTotal') {
        const magnitude = Math.abs(currentValue);
        return isEditing ? (
            <Input
                value={editingValue !== undefined ? editingValue : String(magnitude)}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d*$/.test(v)) setEditingNumericValues((prev) => ({ ...prev, [fieldKey]: v }));
                }}
                onBlur={() => {
                    const raw = editingNumericValues[fieldKey] ?? String(magnitude);
                    const parsed = raw.trim() === '' ? 0 : parseFloat(raw) || 0;
                    if (isCurrentReturnInvoice) {
                        updateField(fieldName, parsed === 0 ? 0 : Math.abs(parsed));
                    } else {
                        updateField(fieldName, parsed === 0 ? 0 : -Math.abs(parsed));
                    }
                    setEditingNumericField(null);
                    setEditingNumericValues((prev) => {
                        const next = { ...prev };
                        delete next[fieldKey];
                        return next;
                    });
                }}
                onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
                autoFocus
                textAlign="right"
                border="none"
                outline="none"
                borderRadius={0}
                color={summaryTextColor}
                bg="transparent"
                _focus={{ outline: 'none', boxShadow: 'none' }}
                onFocus={(e) => {
                    if (editingValue === undefined) setEditingNumericValues((prev) => ({ ...prev, [fieldKey]: String(magnitude) }));
                    (e.target as HTMLInputElement).select();
                }}
                fontSize="md"
                px={0}
                py={0}
                h="auto"
                fontWeight={fontWeight}
            />
        ) : (
            <Tooltip label="Click to edit" placement="top" hasArrow>
                <Box onClick={() => setEditingNumericField(fieldKey)} cursor="pointer" w="100%" textAlign="right" px={2} p={1} fontWeight={fontWeight} color={summaryTextColor}>
                    {fmtUsd(magnitude)}
                </Box>
            </Tooltip>
        );
    }

    if (fieldName === 'orderTotal' && isCurrentReturnInvoice) {
        const magnitude = Math.abs(currentValue);
        return isEditing ? (
            <Input
                value={editingValue !== undefined ? editingValue : String(magnitude)}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d*$/.test(v)) setEditingNumericValues((prev) => ({ ...prev, [fieldKey]: v }));
                }}
                onBlur={() => {
                    const raw = editingNumericValues[fieldKey] ?? String(magnitude);
                    const parsed = raw.trim() === '' ? 0 : parseFloat(raw) || 0;
                    updateField(fieldName, parsed);
                    notifyAmountTeaching(fieldName, previousSummaryValue(fieldKey, magnitude), parsed);
                    setEditingNumericField(null);
                    setEditingNumericValues((prev) => {
                        const next = { ...prev };
                        delete next[fieldKey];
                        return next;
                    });
                }}
                onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
                autoFocus
                textAlign="right"
                border="none"
                outline="none"
                borderRadius={0}
                color={summaryTextColor}
                bg="transparent"
                _focus={{ outline: 'none', boxShadow: 'none' }}
                onFocus={(e) => {
                    if (editingValue === undefined) setEditingNumericValues((prev) => ({ ...prev, [fieldKey]: String(magnitude) }));
                    (e.target as HTMLInputElement).select();
                }}
                fontSize="md"
                px={0}
                py={0}
                h="auto"
                fontWeight={fontWeight}
            />
        ) : (
            <Tooltip label="Click to edit" placement="top" hasArrow>
                <Box onClick={() => beginSummaryEdit(fieldKey, magnitude)} cursor="pointer" w="100%" textAlign="right" px={2} p={1} fontWeight={fontWeight} color={summaryTextColor}>
                    {fmtUsd(magnitude)}
                </Box>
            </Tooltip>
        );
    }

    if (fieldName === 'taxesTotal' && isCurrentReturnInvoice) {
        const magnitude = Math.abs(currentValue);
        return isEditing ? (
            <Input
                value={editingValue !== undefined ? editingValue : String(magnitude)}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d*$/.test(v)) setEditingNumericValues((prev) => ({ ...prev, [fieldKey]: v }));
                }}
                onBlur={() => {
                    const raw = editingNumericValues[fieldKey] ?? String(magnitude);
                    const parsed = raw.trim() === '' ? 0 : parseFloat(raw) || 0;
                    commitTaxValue(parsed);
                    notifyAmountTeaching(fieldName, previousSummaryValue(fieldKey, magnitude), parsed);
                    setEditingNumericField(null);
                    setEditingNumericValues((prev) => {
                        const next = { ...prev };
                        delete next[fieldKey];
                        return next;
                    });
                }}
                onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
                autoFocus
                textAlign="right"
                border="none"
                outline="none"
                borderRadius={0}
                color={summaryTextColor}
                bg="transparent"
                _focus={{ outline: 'none', boxShadow: 'none' }}
                onFocus={(e) => {
                    if (editingValue === undefined) setEditingNumericValues((prev) => ({ ...prev, [fieldKey]: String(magnitude) }));
                    (e.target as HTMLInputElement).select();
                }}
                fontSize="md"
                px={0}
                py={0}
                h="auto"
                fontWeight={fontWeight}
            />
        ) : (
            <Tooltip label={taxesAutoTooltip} placement="top" hasArrow>
                <Box onClick={() => beginSummaryEdit(fieldKey, magnitude)} cursor="pointer" w="100%" textAlign="right" px={2} p={1} fontWeight={fontWeight} color={summaryTextColor}>
                    {fmtUsd(magnitude)}
                </Box>
            </Tooltip>
        );
    }

    const allowNegative =
        fieldName === 'subtotal' ||
        (fieldName === 'taxesTotal' && !isCurrentReturnInvoice) ||
        fieldName === 'freightTotal' ||
        fieldName === 'fuelTotal' ||
        (fieldName === 'orderTotal' && !isCurrentReturnInvoice);
    return isEditing ? (
        <Input
            value={editingValue !== undefined ? editingValue : String(currentValue)}
            onChange={(e) => {
                const v = e.target.value;
                if (v === '' || (allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/).test(v)) setEditingNumericValues((prev) => ({ ...prev, [fieldKey]: v }));
            }}
            onBlur={() => {
                const raw = editingNumericValues[fieldKey] ?? String(currentValue);
                const parsed = raw === '' ? 0 : parseFloat(raw) || 0;
                if (fieldName === 'taxesTotal') commitTaxValue(parsed);
                else updateField(fieldName, parsed);
                notifyAmountTeaching(fieldName, previousSummaryValue(fieldKey, currentValue), parsed);
                setEditingNumericField(null);
                setEditingNumericValues((prev) => {
                    const next = { ...prev };
                    delete next[fieldKey];
                    return next;
                });
            }}
            onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
            autoFocus
            textAlign="right"
            border="none"
            outline="none"
            borderRadius={0}
            color={summaryTextColor}
            bg="transparent"
            _focus={{ outline: 'none', boxShadow: 'none' }}
            onFocus={(e) => {
                if (editingValue === undefined) setEditingNumericValues((prev) => ({ ...prev, [fieldKey]: String(currentValue) }));
                (e.target as HTMLInputElement).select();
            }}
            fontSize="md"
            px={0}
            py={0}
            h="auto"
            fontWeight={fontWeight}
        />
    ) : (
        <Tooltip label={fieldName === 'taxesTotal' ? taxesAutoTooltip : 'Click to edit'} placement="top" hasArrow>
            <Box onClick={() => beginSummaryEdit(fieldKey, currentValue)} cursor="pointer" w="100%" textAlign="right" px={2} p={1} fontWeight={fontWeight} color={summaryTextColor}>
                {fmtUsd(currentValue)}
            </Box>
        </Tooltip>
    );
};

    return renderNumericField;
}
